// SegmentLoader.ts
// Loads historical segments from IndexedDB on demand, with LRU eviction for memory management.
// Maps time ranges to segment lists, hydrates playback buffers, and handles decompression.

import { action, makeObservable, observable, runInAction } from 'mobx';
import { TQuote } from 'src/types';
import { segmentIndexStore } from './SegmentIndexStore';
import { SegmentIngestionPipeline } from './SegmentIngestionPipeline';
import {
    Segment,
    TickSegment,
    CandleSegment,
    TickData,
    CompressionType,
} from './SegmentSchema';

const DEFAULT_MAX_LOADED_SEGMENTS = 20; // Keep max 20 segments in memory
const DEFAULT_PLAYBACK_BUFFER_SIZE = 200000; // 200K ticks in playback buffer (enough for several hours of tick data)

export interface SegmentLoaderConfig {
    maxLoadedSegments?: number;
    playbackBufferSize?: number;
    useWorker?: boolean; // Optional: offload decompression to Web Worker
}

/**
 * Playback buffer: holds decompressed ticks ready for replay.
 */
interface PlaybackBuffer {
    symbol: string;
    granularity: number;
    startEpoch: number;
    endEpoch: number;
    ticks: TickData[];
    loadedSegmentIds: Set<string>;
}

/**
 * SegmentLoader: loads historical segments from IndexedDB on demand,
 * manages memory with LRU eviction, provides playback buffers for replay.
 */
export class SegmentLoader {
    private _config: Required<SegmentLoaderConfig>;
    private _playbackBuffers: Map<string, PlaybackBuffer> = new Map(); // key: `${symbol}-${granularity}`
    private _workerClient?: SegmentWorkerClient; // Optional worker client for decompression

    // Observable state
    loadedSegmentCount = 0;
    totalTicksLoaded = 0;
    lastLoadTime = 0;

    constructor(config: SegmentLoaderConfig = {}) {
        this._config = {
            maxLoadedSegments: config.maxLoadedSegments ?? DEFAULT_MAX_LOADED_SEGMENTS,
            playbackBufferSize: config.playbackBufferSize ?? DEFAULT_PLAYBACK_BUFFER_SIZE,
            useWorker: config.useWorker ?? false,
        };

        makeObservable(this, {
            loadedSegmentCount: observable,
            totalTicksLoaded: observable,
            lastLoadTime: observable,
            loadSegmentsForRange: action,
            clearPlaybackBuffer: action,
            evictLRUSegments: action,
        });
    }

    /**
     * Load segments for a specific time range into playback buffer.
     * Intelligently selects tick vs candle segments based on time range distance.
     * Returns the number of ticks loaded.
     */
    async loadSegmentsForRange(
        symbol: string,
        startEpoch: number,
        endEpoch: number,
        granularity: number = 0,
        skipTrimming: boolean = false
    ): Promise<number> {
        console.log('[SegmentLoader] Loading segments for range:', {
            symbol,
            startEpoch: new Date(startEpoch),
            endEpoch: new Date(endEpoch),
            granularity,
        });

        // Auto-select optimal granularity based on distance from now
        const { selectGranularityForRange } = await import('./CandleAggregator');
        const optimalGranularity = selectGranularityForRange(startEpoch, endEpoch);
        
        // Use optimal granularity if requesting tick data (granularity = 0)
        const effectiveGranularity = granularity === 0 ? optimalGranularity : granularity;
        
        if (effectiveGranularity !== granularity) {
            console.log('[SegmentLoader] Auto-selected granularity:', effectiveGranularity, 'seconds (instead of ticks)');
        }

        // Query segment index for matching segments with effective granularity
        const segmentEntries = segmentIndexStore.querySegments(symbol, startEpoch, endEpoch, effectiveGranularity);
        
        if (segmentEntries.length === 0) {
            console.warn('[SegmentLoader] No segments found for range with granularity:', effectiveGranularity);
            return 0;
        }

        console.log('[SegmentLoader] Found', segmentEntries.length, 'segments');

        // Get or create playback buffer
        const bufferKey = this._getBufferKey(symbol, granularity);
        let buffer = this._playbackBuffers.get(bufferKey);
        
        if (!buffer) {
            buffer = {
                symbol,
                granularity,
                startEpoch,
                endEpoch,
                ticks: [],
                loadedSegmentIds: new Set(),
            };
            this._playbackBuffers.set(bufferKey, buffer);
        }

        // Load segments that aren't already loaded
        const toLoad = segmentEntries.filter(entry => !buffer!.loadedSegmentIds.has(entry.segmentId));
        
        if (toLoad.length === 0) {
            console.log('[SegmentLoader] All segments already loaded');
            return buffer.ticks.length;
        }

        console.log('[SegmentLoader] Loading', toLoad.length, 'new segments');

        // Check if we need to evict before loading
        await this._checkAndEvictSegments(toLoad.length);

        // Load segments in parallel
        const loadPromises = toLoad.map(entry => this._loadAndDecompressSegment(entry.segmentId));
        const segments = await Promise.all(loadPromises);

        // Merge ticks into playback buffer
        let ticksAdded = 0;
        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            if (!segment) continue;

            const entry = toLoad[i];
            buffer.loadedSegmentIds.add(entry.segmentId);

            // Extract and filter ticks for requested range
            const segmentTicks = this._extractTicksFromSegment(segment, startEpoch, endEpoch);
            buffer.ticks.push(...segmentTicks);
            ticksAdded += segmentTicks.length;
        }

        // Sort ticks by epoch (segments may be out of order)
        buffer.ticks.sort((a, b) => a.epoch - b.epoch);

        // Update buffer bounds
        if (buffer.ticks.length > 0) {
            buffer.startEpoch = buffer.ticks[0].epoch;
            buffer.endEpoch = buffer.ticks[buffer.ticks.length - 1].epoch;
            
            // Log sample tick epochs to diagnose tick vs minute data
            if (buffer.ticks.length >= 5) {
                const sample = buffer.ticks.slice(0, 5);
                console.log('[SegmentLoader] 📊 SAMPLE TICK EPOCHS FROM SEGMENTS:');
                sample.forEach((tick, i) => {
                    console.log(`  [${i}]: ${new Date(tick.epoch).toISOString()} (epoch: ${tick.epoch})`);
                });
                const interval = sample[1].epoch - sample[0].epoch;
                console.log(`  Interval between [0] and [1]: ${interval}ms`,
                    interval === 1000 ? '(TICK DATA ✓)' :
                    interval >= 60000 ? '(MINUTE CANDLE DATA ⚠️)' :
                    interval < 1000 ? '(sub-second ticks ✓)' : '(irregular)');
            }
        }

        // Log buffer state before trimming
        console.log('[SegmentLoader] Buffer before trim:', buffer.ticks.length, 'ticks, range:', 
            buffer.ticks.length > 0 ? new Date(buffer.ticks[0].epoch) : 'empty', 'to',
            buffer.ticks.length > 0 ? new Date(buffer.ticks[buffer.ticks.length - 1].epoch) : 'empty');

        // Trim buffer if exceeding size limit (skip for replay mode to preserve all historical data)
        if (!skipTrimming) {
            this._trimPlaybackBuffer(buffer);
            console.log('[SegmentLoader] Buffer after trim:', buffer.ticks.length, 'ticks, range:', 
                buffer.ticks.length > 0 ? new Date(buffer.ticks[0].epoch) : 'empty', 'to',
                buffer.ticks.length > 0 ? new Date(buffer.ticks[buffer.ticks.length - 1].epoch) : 'empty');
        } else {
            console.log('[SegmentLoader] Skipping buffer trim for replay mode - preserving all', buffer.ticks.length, 'ticks');
        }

        runInAction(() => {
            this.loadedSegmentCount = this._countLoadedSegments();
            this.totalTicksLoaded = this._countTotalTicks();
            this.lastLoadTime = Date.now();
        });

        console.log('[SegmentLoader] Loaded', ticksAdded, 'ticks, buffer now has', buffer.ticks.length);
        return ticksAdded;
    }

    /**
     * Get ticks from playback buffer for a specific range.
     */
    getTicksFromBuffer(
        symbol: string,
        startEpoch: number,
        endEpoch: number,
        granularity: number = 0
    ): TickData[] {
        const bufferKey = this._getBufferKey(symbol, granularity);
        const buffer = this._playbackBuffers.get(bufferKey);
        
        if (!buffer) return [];

        // Log buffer bounds to debug range extraction
        if (buffer.ticks.length > 0) {
            const bufferFirstEpoch = buffer.ticks[0].epoch;
            const bufferLastEpoch = buffer.ticks[buffer.ticks.length - 1].epoch;
            console.log('[SegmentLoader] getTicksFromBuffer: buffer has', buffer.ticks.length, 'ticks from', new Date(bufferFirstEpoch), 'to', new Date(bufferLastEpoch), 'querying', new Date(startEpoch), 'to', new Date(endEpoch));
        }

        // Binary search for efficient range extraction
        const startIdx = this._binarySearchStart(buffer.ticks, startEpoch);
        const endIdx = this._binarySearchEnd(buffer.ticks, endEpoch);

        if (startIdx === -1 || endIdx === -1) {
            console.warn('[SegmentLoader] getTicksFromBuffer: binary search failed, startIdx=', startIdx, 'endIdx=', endIdx);
            return [];
        }

        const result = buffer.ticks.slice(startIdx, endIdx + 1);
        console.log('[SegmentLoader] getTicksFromBuffer: returning', result.length, 'ticks from', result.length > 0 ? new Date(result[0].epoch) : 'empty', 'to', result.length > 0 ? new Date(result[result.length - 1].epoch) : 'empty');
        return result;
    }

    /**
     * Convert TickData array to TQuote array for replay.
     * Adds sub-millisecond offsets to ticks with duplicate epochs to preserve ordering.
     */
    ticksToQuotes(ticks: TickData[]): TQuote[] {
        // Track duplicate epochs and add offsets to distinguish them
        // This handles data stored with second-level precision from Deriv API
        let lastEpochBase = -1;
        let offset = 0;
        
        return ticks.map(tick => {
            // Get base epoch (rounded to nearest second)
            const epochBase = Math.floor(tick.epoch / 1000) * 1000;
            
            // Reset offset for new seconds, increment for same second
            if (epochBase === lastEpochBase) {
                offset++;
            } else {
                lastEpochBase = epochBase;
                offset = 0;
            }
            
            // Add offset to create unique timestamps (max 999 per second)
            const uniqueEpoch = epochBase + Math.min(offset, 999);
            
            // When OHLC is available, use it. Otherwise, default all OHLC to close price
            // to prevent rendering a giant candle spanning from 0/undefined to the actual price.
            const hasOHLC = tick.open !== undefined && tick.high !== undefined && tick.low !== undefined;
            return {
                Date: new Date(uniqueEpoch).toISOString().substring(0, 19),
                DT: new Date(uniqueEpoch),
                Open: hasOHLC ? tick.open : tick.price,
                High: hasOHLC ? tick.high : tick.price,
                Low: hasOHLC ? tick.low : tick.price,
                Close: tick.price,
                Volume: tick.volume,
            };
        });
    }

    /**
     * Clear playback buffer for a symbol/granularity.
     */
    clearPlaybackBuffer(symbol: string, granularity: number = 0): void {
        const bufferKey = this._getBufferKey(symbol, granularity);
        const buffer = this._playbackBuffers.get(bufferKey);
        
        if (buffer) {
            // Unload segments from index store
            for (const segmentId of buffer.loadedSegmentIds) {
                segmentIndexStore.unloadSegment(segmentId);
            }
            
            this._playbackBuffers.delete(bufferKey);
            
            runInAction(() => {
                this.loadedSegmentCount = this._countLoadedSegments();
                this.totalTicksLoaded = this._countTotalTicks();
            });
            
            console.log('[SegmentLoader] Cleared playback buffer:', bufferKey);
        }
    }

    /**
     * Clear all playback buffers.
     */
    clearAllBuffers(): void {
        for (const [key] of this._playbackBuffers.entries()) {
            const [symbol, granularityStr] = key.split('-');
            this.clearPlaybackBuffer(symbol, parseInt(granularityStr));
        }
    }

    /**
     * Get playback buffer stats.
     */
    getBufferStats(): { symbol: string; granularity: number; tickCount: number; segmentCount: number }[] {
        const stats = [];
        for (const [, buffer] of this._playbackBuffers.entries()) {
            stats.push({
                symbol: buffer.symbol,
                granularity: buffer.granularity,
                tickCount: buffer.ticks.length,
                segmentCount: buffer.loadedSegmentIds.size,
            });
        }
        return stats;
    }

    /**
     * Manually trigger LRU eviction to free memory.
     */
    evictLRUSegments(count: number = 1): void {
        const candidates = segmentIndexStore.getLRUCandidates(count);
        
        for (const entry of candidates) {
            // Find buffer containing this segment
            for (const [, buffer] of this._playbackBuffers.entries()) {
                if (buffer.loadedSegmentIds.has(entry.segmentId)) {
                    buffer.loadedSegmentIds.delete(entry.segmentId);
                    segmentIndexStore.unloadSegment(entry.segmentId);
                    console.log('[SegmentLoader] Evicted segment:', entry.segmentId);
                    break;
                }
            }
        }

        runInAction(() => {
            this.loadedSegmentCount = this._countLoadedSegments();
        });
    }

    // Private methods

    private _getBufferKey(symbol: string, granularity: number): string {
        return `${symbol}-${granularity}`;
    }

    // Lazily create a worker client if enabled and supported
    private _getWorkerClient(): SegmentWorkerClient | null {
        if (!this._config.useWorker) return null;
        if (typeof Worker === 'undefined') return null;

        if (this._workerClient) return this._workerClient;

        try {
            this._workerClient = new SegmentWorkerClient();
            console.log('[SegmentLoader] Worker client initialized for decompression');
            return this._workerClient;
        } catch (err) {
            console.warn('[SegmentLoader] Failed to initialize worker client, falling back to main thread', err);
            this._config = { ...this._config, useWorker: false };
            return null;
        }
    }

    private async _decompressTicksWithWorker(ticks: TickData[]): Promise<TickData[] | null> {
        const client = this._getWorkerClient();
        if (!client) return null;
        try {
            return await client.decompressDeltaTicks(ticks);
        } catch (err) {
            console.warn('[SegmentLoader] Worker decompression failed, falling back to main thread', err);
            return null;
        }
    }

    private async _loadAndDecompressSegment(segmentId: string): Promise<Segment | null> {
        const segment = await segmentIndexStore.loadSegment(segmentId);
        if (!segment) return null;

        // Decompress if needed
        if (segment.metadata.type === 'tick') {
            const tickSegment = segment as TickSegment;
            if (tickSegment.metadata.compression === 'delta') {
                const workerResult = await this._decompressTicksWithWorker(tickSegment.ticks);
                if (workerResult) {
                    tickSegment.ticks = workerResult;
                } else {
                    tickSegment.ticks = SegmentIngestionPipeline.decompressDeltaTicks(tickSegment.ticks);
                }
                tickSegment.metadata.compression = 'none' as CompressionType;
            }
        }

        return segment;
    }

    private _extractTicksFromSegment(segment: Segment, startEpoch: number, endEpoch: number): TickData[] {
        if (segment.metadata.type === 'tick') {
            const tickSegment = segment as TickSegment;
            return tickSegment.ticks.filter(tick => tick.epoch >= startEpoch && tick.epoch <= endEpoch);
        } else if (segment.metadata.type === 'candle') {
            // Preserve OHLC data from candle segments so replay can render candlesticks
            const candleSegment = segment as CandleSegment;
            const ticks: TickData[] = candleSegment.candles
                .filter(candle => candle.epoch >= startEpoch && candle.epoch <= endEpoch)
                .map(candle => ({
                    epoch: candle.epoch,
                    price: candle.close,
                    volume: candle.volume,
                    open: candle.open,
                    high: candle.high,
                    low: candle.low,
                }));
            return ticks;
        }
        return [];
    }

    private _trimPlaybackBuffer(buffer: PlaybackBuffer): void {
        const beforeCount = buffer.ticks.length;
        if (buffer.ticks.length > this._config.playbackBufferSize) {
            const excessCount = buffer.ticks.length - this._config.playbackBufferSize;
            const removedStartTime = buffer.ticks[0].epoch;
            const removedEndTime = buffer.ticks[excessCount - 1].epoch;
            
            // Keep the most recent ticks by removing from the beginning
            buffer.ticks.splice(0, excessCount);
            console.warn('[SegmentLoader] TRIMMED playback buffer:', buffer.symbol);
            console.warn('[SegmentLoader] Removed', excessCount, 'ticks from', new Date(removedStartTime), 'to', new Date(removedEndTime));
            console.warn('[SegmentLoader] Buffer now has', buffer.ticks.length, 'ticks from', new Date(buffer.ticks[0].epoch), 'to', new Date(buffer.ticks[buffer.ticks.length - 1].epoch));
            
            // Update buffer bounds after trimming
            if (buffer.ticks.length > 0) {
                buffer.startEpoch = buffer.ticks[0].epoch;
            }
        } else {
            console.log('[SegmentLoader] Buffer size OK:', beforeCount, 'ticks (limit:', this._config.playbackBufferSize, ') - no trimming needed');
        }
    }

    private async _checkAndEvictSegments(toLoadCount: number): Promise<void> {
        const currentLoaded = this._countLoadedSegments();
        const futureLoaded = currentLoaded + toLoadCount;

        if (futureLoaded > this._config.maxLoadedSegments) {
            const toEvict = futureLoaded - this._config.maxLoadedSegments;
            console.log('[SegmentLoader] Evicting', toEvict, 'LRU segments to make room');
            this.evictLRUSegments(toEvict);
        }
    }

    private _countLoadedSegments(): number {
        let count = 0;
        for (const [, buffer] of this._playbackBuffers.entries()) {
            count += buffer.loadedSegmentIds.size;
        }
        return count;
    }

    private _countTotalTicks(): number {
        let count = 0;
        for (const [, buffer] of this._playbackBuffers.entries()) {
            count += buffer.ticks.length;
        }
        return count;
    }

    private _binarySearchStart(ticks: TickData[], targetEpoch: number): number {
        let left = 0;
        let right = ticks.length - 1;
        let result = -1;

        while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            if (ticks[mid].epoch >= targetEpoch) {
                result = mid;
                right = mid - 1;
            } else {
                left = mid + 1;
            }
        }

        return result;
    }

    private _binarySearchEnd(ticks: TickData[], targetEpoch: number): number {
        let left = 0;
        let right = ticks.length - 1;
        let result = -1;

        while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            if (ticks[mid].epoch <= targetEpoch) {
                result = mid;
                left = mid + 1;
            } else {
                right = mid - 1;
            }
        }

        return result;
    }
}

// Singleton instance
export const segmentLoader = new SegmentLoader();

// Lightweight worker client for optional decompression offloading
class SegmentWorkerClient {
    private worker: Worker;
    private requestId = 0;
    private pending: Map<number, (value: TickData[]) => void> = new Map();
    private pendingReject: Map<number, (reason?: any) => void> = new Map();

    constructor() {
        this.worker = new Worker(new URL('./workers/segment-loader.worker.ts', import.meta.url), { type: 'module' });
        this.worker.onmessage = (event: MessageEvent) => {
            const { id, payload, error } = event.data || {};
            if (id == null) return;
            if (error) {
                const reject = this.pendingReject.get(id);
                if (reject) reject(error);
                this.pending.delete(id);
                this.pendingReject.delete(id);
                return;
            }
            const resolver = this.pending.get(id);
            if (resolver) resolver(payload?.ticks ?? []);
            this.pending.delete(id);
            this.pendingReject.delete(id);
        };
    }

    decompressDeltaTicks(ticks: TickData[]): Promise<TickData[]> {
        return new Promise((resolve, reject) => {
            const id = ++this.requestId;
            this.pending.set(id, resolve);
            this.pendingReject.set(id, reject);
            this.worker.postMessage({ id, type: 'DECOMPRESS_DELTA_TICKS', payload: { ticks } });
        });
    }
}
